import { useState, useEffect, useMemo, useRef } from 'react'
import { useOutletContext, useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getAggregateClicksBreakdown, getAggregateFormsBreakdown, getFormsDetails, type FormsBreakdownPage, type FormsBreakdownResponse, type FormsDetailsResponse } from '@/lib/api/external-api'
import { pickUniqueSubmissions } from '@/lib/api/formsFields'
import { getConnectorsByCategory } from '@/lib/config/connectors'
import { interceptDemoAPI, demoGenerators } from '@/lib/utils/demoData'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useFilter } from '@/lib/contexts/FilterContext'
import { useWorkspaceSafe } from '@/lib/contexts/WorkspaceContext'
import { useURLState } from '@/hooks/useURLState'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { uniqueForGroupSelection } from '@/lib/calculations/breakdownTotals'
import { formatUtmLabel } from '@/lib/utils/utmLabel'
import { matchesPageSearch } from '@/lib/utils/pagePath'
import { TrafficSourcesList } from '@/components/shared/TrafficSourcesList'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { FORM_DETAIL_PARAM } from '@/lib/routing/analyticsTabs'
import { FormsNoFormsDetected } from '@/components/shared/analytics/FormsEmpty'
import { pickFormIntentPage, type FormIntentPage } from '@/lib/calculations/formsEmpty'
import { periodNoun } from '@/lib/utils/analyticsRange'
import { LoadingState } from '@/components/shared/layout'
import {
  RaisedCard,
  CardHead,
  CardEmpty,
  FilterChip,
  Segmented,
  Pagination,
} from '@/components/dashboard/analyticsTabs/TabPrimitives'
import { BrandIcon } from '@/components/ui/brand-icons'
import { FORM_CONV_RATE_TOOLTIP, UNIQUE_SUBMISSIONS_TOOLTIP } from '@/lib/constants/metricsCopy'
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowDown01Icon, InformationCircleIcon, Search01Icon } from "@hugeicons/core-free-icons"
import { getPeriodUniqueVisitorTotal } from '@/lib/calculations/breakdownTotals'
import { sentenceLabel } from '@/components/shared/analytics/PagesReport'

interface DashboardContext {
  propertyId?: string
  token?: string
  period?: string
  filters?: any[]
  dashboardData?: any
}

// Convert frontend period format to external API format
const toExternalApiPeriod = (period: string): string => {
  // Handle custom date range format: "YYYY-MM-DD:YYYY-MM-DD"
  if (period.includes(':')) {
    const [start, end] = period.split(':')
    // Calculate the difference to determine granularity
    const startDate = new Date(start)
    const endDate = new Date(end)
    const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    
    // Choose granularity based on date range length
    let granularity = 'day'
    if (diffDays <= 2) {
      granularity = 'hour'
    } else if (diffDays > 90) {
      granularity = 'week'
    }
    
    // Format: granularity_CUSTOM_start_to_end (matching API expectation)
    return `${granularity}_CUSTOM_${start}_to_${end}`
  }

  const periodMap: Record<string, string> = {
    '1d': 'hour_PAST_24_H',
    '7d': 'day_PAST_7_D',
    '30d': 'day_PAST_30_D',
    '90d': 'day_PAST_3_M',
    '180d': 'week_PAST_6_M',
    '365d': 'week_PAST_12_M',
    '12m': 'month_PAST_12_M',
    'ytd': 'day_PAST_12_M',
    'mtd': 'day_PAST_30_D',
  }
  return periodMap[period] || 'day_PAST_7_D'
}

/**
 * Form submissions section of the "Clicks & forms" tab (was the Forms tab):
 * its own well under the clicks well, below the page's single headline. Its
 * expanded row lives in `?formDetailPage=` because Clicks owns `?detailPage=`.
 */
const isGenericFormTarget = (target?: string | null) => !target || /^(form|submit)$/i.test(target.trim())

export default function FormsSection() {
  const ctx = useOutletContext<DashboardContext>()
  const params = useParams()
  const { token: authToken } = useAuth()
  const { filters: contextFilters } = useFilter()
  const { state } = useURLState()
  
  // Use token from context (share mode) or auth (regular mode)
  const token = ctx.token || authToken
  const workspaceContext = useWorkspaceSafe()
  const propertyIds = workspaceContext?.selectedPropertyIds?.length
    ? workspaceContext.selectedPropertyIds
    : ctx.propertyId ? [ctx.propertyId] : []
  
  // Get the base path for tab navigation
  const getBasePath = () => {
    if (params.magicId && params.password) {
      return `/s/${params.magicId}/${params.password}`;
    }
    if (params.workspaceId) {
      return `/workspace/${params.workspaceId}`;
    }
    if (params.propertySlugId) {
      return `/p/${params.propertySlugId}`;
    }
    if (params.domain) {
      return `/d/${params.domain}`;
    }
    return '';
  };
  const basePath = getBasePath();
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [formData, setFormData] = useState<FormsBreakdownPage[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [summary, setSummary] = useState<FormsBreakdownResponse['summary'] | null>(null)
  const groupSummary = useMemo(() => summary?.groups ?? {}, [summary])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const isInitialLoad = useRef(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set(['all']))
  const [activeSource, setActiveSource] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const batchSize = 100

  // Client filtering uses live searchTerm — reset page/groups immediately so a
  // stale page index or group chip can't hide matches while debounce catches up.
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
    setSelectedGroups((prev) => (prev.size === 1 && prev.has('all') ? prev : new Set(['all'])))
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
  }

  // Source filter chips — derived from actual submission data, not config. A
  // webhook-based connector (JotForm, Tally) only proves itself by producing
  // submissions, so we hide its chip until at least one submission arrives. Brand
  // icons + display labels come from the connectors config so adding a new form
  // provider only requires registering it in src/lib/config/connectors.ts.
  const sourceOptions = useMemo(() => {
    const formConnectorConfigs = getConnectorsByCategory('forms')
    return [
      { id: 'all', label: 'All Sources' },
      { id: 'native', label: 'Native Script' },
      ...Object.keys(groupSummary)
        .filter((group) => group !== 'native' && group !== 'all')
        .sort((a, b) => (groupSummary[b]?.total_submissions ?? 0) - (groupSummary[a]?.total_submissions ?? 0))
        .map((group) => {
          const cfg = formConnectorConfigs.find((c) => String(c.id) === group)
          return { id: group, label: cfg?.name ?? group, brand: cfg?.brandKey as string | undefined }
        }),
    ] as { id: string; label: string; brand?: string }[]
  }, [groupSummary])

  // If the active source chip disappears (e.g., date range change clears its data),
  // fall back to All Sources so the filter doesn't get stuck on something invisible.
  useEffect(() => {
    if (activeSource !== 'all' && !sourceOptions.some((opt) => opt.id === activeSource)) {
      setActiveSource('all')
    }
  }, [activeSource, sourceOptions])

  // Detail drawer state
  const selectedPage = searchParams.get(FORM_DETAIL_PARAM)
  const [formDetails, setFormDetails] = useState<FormsDetailsResponse | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [showAllForms, setShowAllForms] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      if (!propertyIds.length || !token) {
        setIsLoading(false)
        return
      }

      if (isInitialLoad.current) {
        setIsLoading(true)
      } else {
        setIsRefreshing(true)
      }
      setCurrentPage(1)
      try {
        const externalApiPeriod = toExternalApiPeriod(state.period || '7d')

        const filterParams: Record<string, string> = {}
        for (const f of state.filters) {
          if (f.operator === ':') filterParams[f.field] = f.value
        }

        const response = await interceptDemoAPI(
          propertyIds[0],
          () => getAggregateFormsBreakdown(propertyIds, externalApiPeriod, token, batchSize, ctx.propertyId, filterParams, debouncedSearch || undefined, 0),
          demoGenerators.forms
        )

        const rows = response.data ?? []
        setFormData(rows)
        setTotalCount(response.meta.total_count ?? rows.length)
        setSummary(response.summary ?? null)
      } catch (error) {
        console.error('Error fetching form stats:', error)
        setFormData([])
        setSummary(null)
      } finally {
        setIsLoading(false)
        setIsRefreshing(false)
        isInitialLoad.current = false
      }
    }

    fetchData()
  }, [propertyIds.join(','), token, state.period, activeSource, JSON.stringify(state.filters), debouncedSearch])

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
    const requiredItems = newPage * itemsPerPage
    if (requiredItems > formData.length && formData.length < totalCount && !isFetchingMore) {
      setIsFetchingMore(true)
      const offset = formData.length
      const externalApiPeriod = toExternalApiPeriod(state.period || '7d')
      const filterParams: Record<string, string> = {}
      for (const f of state.filters) {
        if (f.operator === ':') filterParams[f.field] = f.value
      }
      getAggregateFormsBreakdown(propertyIds, externalApiPeriod, token!, batchSize, ctx.propertyId, filterParams, debouncedSearch || undefined, offset)
        .then((response) => {
          setFormData((prev) => [...prev, ...response.data])
          setTotalCount(response.meta.total_count ?? (offset + response.data.length))
        })
        .catch((error) => console.error('Error fetching more forms:', error))
        .finally(() => setIsFetchingMore(false))
    }
  }

  // Fetch form details when a page is selected
  useEffect(() => {
    const fetchFormDetails = async () => {
      if (!selectedPage || !propertyIds.length || !token) {
        setFormDetails(null)
        return
      }

      setIsLoadingDetails(true)
      try {
        const externalApiPeriod = toExternalApiPeriod(state.period || '7d')
        const filterParams: Record<string, string> = {}
        for (const f of state.filters) {
          if (f.operator === ':') filterParams[f.field] = f.value
          else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value
        }
        const response = await getFormsDetails(selectedPage, externalApiPeriod, propertyIds[0], token, 20, activeSource, filterParams)
        setFormDetails(response)
      } catch (error) {
        console.error('Error fetching form details:', error)
        setFormDetails(null)
      } finally {
        setIsLoadingDetails(false)
      }
    }

    fetchFormDetails()
  }, [selectedPage, propertyIds[0], token, state.period, activeSource, JSON.stringify(state.filters)])

  const togglePageDetails = (page: string) => {
    const params = new URLSearchParams(searchParams)
    if (selectedPage === page) {
      // Close if already open
      params.delete(FORM_DETAIL_PARAM)
      setShowAllForms(false)
    } else {
      // Open new page
      params.set(FORM_DETAIL_PARAM, page)
      setShowAllForms(false)
    }
    setSearchParams(params)
  }

  // Toggle group filter
  const toggleGroup = (group: string) => {
    const newGroups = new Set(selectedGroups)

    if (group === 'all') {
      setSelectedGroups(new Set(['all']))
      setCurrentPage(1)
      return
    }

    newGroups.delete('all')

    if (newGroups.has(group)) {
      newGroups.delete(group)
      if (newGroups.size === 0) {
        newGroups.add('all')
      }
    } else {
      newGroups.add(group)
    }

    setSelectedGroups(newGroups)
    setCurrentPage(1)
  }

  // Get available groups sorted by total submissions
  const availableGroups = Object.entries(groupSummary)
    .sort((a, b) => b[1].total_submissions - a[1].total_submissions)
    .map(([group]) => group)

  // Filter by group chips + client search (also covers demo / backend gaps).
  const filteredData = formData.filter((page) => {
    const groupOk = selectedGroups.has('all') || selectedGroups.has(page.page_group)
    const searchOk = matchesPageSearch(page.page, searchTerm, [page.page_group])
    return groupOk && searchOk
  })

  const isGroupFiltered = !selectedGroups.has('all')

  // Unique submissions dedupe per visitor, so they can never be summed across
  // page rows — someone who submits on /pricing and /contact appears in both.
  // Read the deduped totals the endpoint returns instead; they also cover the
  // whole period rather than just the first loaded batch of rows.
  const { totalSubmissions, uniqueSubmissions } = useMemo(() => {
    if (isGroupFiltered) {
      // Submission events add up across groups; unique submitters don't (one person
      // can submit in two sections), so they come from the deduped per-group or
      // summary value, or are unavailable (null) for a multi-group pick.
      const totalSubmissions = Array.from(selectedGroups).reduce(
        (sum, group) => sum + (groupSummary[group]?.total_submissions ?? 0),
        0,
      )
      const groupUniques = Object.fromEntries(
        Object.entries(groupSummary).map(([group, g]) => [group, pickUniqueSubmissions(g)]),
      )
      return {
        totalSubmissions,
        uniqueSubmissions: uniqueForGroupSelection(selectedGroups, groupUniques, pickUniqueSubmissions(summary)),
      }
    }
    return {
      totalSubmissions: summary?.total_submissions ?? 0,
      uniqueSubmissions: pickUniqueSubmissions(summary) as number | null,
    }
  }, [isGroupFiltered, selectedGroups, groupSummary, summary])

  // No submission at all in the range, before any search, source or section
  // narrows it: the table gives way to "No forms detected". Its line names the
  // most clicked form-like page, read once from the clicks breakdown.
  const noFormsDetected =
    !isLoading && !debouncedSearch && !searchTerm && activeSource === 'all' && !isGroupFiltered &&
    formData.length === 0 && (summary?.total_submissions ?? 0) === 0
  const [formIntentPage, setFormIntentPage] = useState<FormIntentPage | null>(null)
  const clicksPropertyKey = propertyIds.join(',')
  const clicksPeriod = state.period || '7d'
  const clicksFiltersKey = JSON.stringify(state.filters)
  const clicksSharePropertyId = ctx.propertyId
  useEffect(() => {
    const ids = clicksPropertyKey ? clicksPropertyKey.split(',') : []
    if (!noFormsDetected || !ids.length || !token) {
      setFormIntentPage(null)
      return
    }
    let stale = false
    const filterParams: Record<string, string> = {}
    for (const f of JSON.parse(clicksFiltersKey) as Array<{ field: string; operator: string; value: string }>) {
      if (f.operator === ':') filterParams[f.field] = f.value
    }
    interceptDemoAPI(
      ids[0],
      () => getAggregateClicksBreakdown(ids, toExternalApiPeriod(clicksPeriod), token, 100, clicksSharePropertyId, filterParams, undefined, 0),
      demoGenerators.clicks
    )
      .then((response) => {
        if (!stale) setFormIntentPage(pickFormIntentPage(response.data ?? []))
      })
      .catch(() => {
        // The sentence falls back to its plain form; nothing else depends on it.
        if (!stale) setFormIntentPage(null)
      })
    return () => {
      stale = true
    }
  }, [noFormsDetected, clicksPropertyKey, token, clicksPeriod, clicksFiltersKey, clicksSharePropertyId])

  // Get top form across all filtered pages
  const topForm = (() => {
    const formCounts: Record<string, number> = {}
    filteredData.forEach((page) => {
      page.top_forms.forEach((form) => {
        if (isGenericFormTarget(form.target) && page.top_forms.some((f) => !isGenericFormTarget(f.target))) return
        formCounts[form.target] = (formCounts[form.target] || 0) + form.submissions
      })
    })

    let maxForm = ''
    let maxCount = 0
    Object.entries(formCounts).forEach(([form, count]) => {
      if (count > maxCount) {
        maxCount = count
        maxForm = form
      }
    })
    return maxForm
  })()

  // Sort and paginate
  const sortedData = [...filteredData].sort((a, b) => b.total_submissions - a.total_submissions)
  const paginatedData = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
  // Group chips filter client-side only; search is applied server-side (debouncedSearch),
  // so its total_count already reflects the filter. Trust the server total for search so
  // matches beyond the first loaded batch stay reachable.
  const paginationTotal = isGroupFiltered
    ? sortedData.length
    : Math.max(totalCount, sortedData.length)
  const totalPages = Math.ceil(paginationTotal / itemsPerPage)

  if (isLoading) {
    return (
      <div className="well">
        <LoadingState message="Loading form submissions..." size="md" />
      </div>
    )
  }

  // Conversion = people who submitted ÷ unique visitors in the period (deduped
  // totals, never summed rows). Submissions ÷ unique submissions was "200%":
  // repeat submits per person, not conversion.
  const periodVisitors = getPeriodUniqueVisitorTotal(ctx.dashboardData?.metrics)
  const convRate =
    uniqueSubmissions !== null && periodVisitors > 0
      ? `${((uniqueSubmissions / periodVisitors) * 100).toFixed(uniqueSubmissions / periodVisitors < 0.1 ? 2 : 1)}%`
      : '--'
  const infoTip = (text: string) => (
    <Tooltip>
      <TooltipTrigger>
        <HugeiconsIcon icon={InformationCircleIcon} size={12} className="text-muted-foreground/60" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px] text-xs leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  )

  return (
    <TooltipProvider>
          {/* Forms (was its own tab): second well, under the Clicks headline */}
          <div className="well flex flex-col gap-2">
            <RaisedCard>
              <div className="flex overflow-x-auto scrollbar-hide">
                {[
                  { label: 'Submissions', value: formatNumeric(totalSubmissions) },
                  {
                    label: <>Unique submissions {infoTip(uniqueSubmissions === null ? 'Not available for a multi-section selection: a person who submits in several sections cannot be deduplicated. Select one section, or all.' : UNIQUE_SUBMISSIONS_TOOLTIP)}</>,
                    value: uniqueSubmissions === null ? '--' : formatNumeric(uniqueSubmissions),
                  },
                  { label: <>Conversion rate {infoTip(FORM_CONV_RATE_TOOLTIP)}</>, value: convRate },
                  { label: 'Pages', value: filteredData.length },
                  { label: 'Top form', value: topForm || '--', wide: true },
                ].map((tile, i) => (
                  <div
                    key={i}
                    className={`flex min-w-[140px] flex-col gap-1.5 px-[18px] py-4 ${tile.wide ? 'flex-[1.6]' : 'flex-1'} ${i > 0 ? 'border-l border-border/60' : ''}`}
                  >
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">{tile.label}</span>
                    <span className="truncate text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums" title={String(tile.value)}>
                      {tile.value}
                    </span>
                  </div>
                ))}
              </div>
            </RaisedCard>

            {noFormsDetected ? (
              <FormsNoFormsDetected
                domain={ctx.dashboardData?.metrics?.domain || ''}
                periodNoun={periodNoun(state.period || '7d')}
                formPage={formIntentPage}
                onConnect={basePath.startsWith('/s/') ? undefined : () => navigate(`${basePath}/connectors`)}
              />
            ) : (
            <RaisedCard className={isRefreshing ? 'opacity-70 transition-opacity' : undefined}>
              <CardHead title="Form submissions">
                <Segmented
                  value={activeSource}
                  onChange={(id) => {
                    setActiveSource(id)
                    setCurrentPage(1)
                  }}
                  options={sourceOptions.map((opt) => ({
                    value: opt.id,
                    label: (
                      <>
                        {opt.brand && <BrandIcon brand={opt.brand as any} className="h-3 w-3" />}
                        {opt.id === 'all' ? 'All sources' : opt.id === 'native' ? 'Native script' : opt.label}
                      </>
                    ),
                  }))}
                />
                <div className="relative w-full sm:w-56">
                  <HugeiconsIcon icon={Search01Icon} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search pages"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="h-8 pl-8 text-xs"
                  />
                </div>
              </CardHead>

              {availableGroups.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 px-[18px] pb-3">
                  <FilterChip active={selectedGroups.has('all')} onClick={() => toggleGroup('all')}>
                    All pages <span className="tabular-nums opacity-70">{formData.length}</span>
                  </FilterChip>
                  {availableGroups.map((group) => (
                    <FilterChip key={group} active={selectedGroups.has(group)} onClick={() => toggleGroup(group)}>
                      {sentenceLabel(group)}
                      <span className="tabular-nums opacity-70">{groupSummary[group].pages}</span>
                    </FilterChip>
                  ))}
                </div>
              )}

              <div className="flex border-y border-border/60 bg-muted/40 px-[18px] py-2 text-xs text-muted-foreground">
                <span className="flex-1 pl-6">Page</span>
                <span className="w-[100px] shrink-0 text-right">Submissions</span>
                <span className="hidden w-[200px] shrink-0 pl-6 sm:block">Top form</span>
                <span className="hidden w-[140px] shrink-0 text-right md:block">Unique submissions</span>
              </div>

              {paginatedData.length === 0 ? (
                <CardEmpty>{searchTerm ? 'No forms match your search' : 'No form data available'}</CardEmpty>
              ) : (
                <div className="flex flex-col">
                  {paginatedData.map((page, index) => {
                    // The script also files each submit as a generic "Form"; name the real one.
                    const rowForm = page.top_forms.find((f) => !isGenericFormTarget(f.target)) ?? page.top_forms[0]
                    const isExpanded = selectedPage === page.page
                    return (
                      <div key={`row-${index}`} className="border-b border-border/60 last:border-b-0">
                        <button
                          type="button"
                          onClick={() => togglePageDetails(page.page)}
                          aria-expanded={isExpanded}
                          className="group flex min-h-12 w-full items-center px-[18px] py-2 text-left transition-colors hover:bg-muted/40"
                        >
                          <span className="flex min-w-0 flex-1 items-center gap-2">
                            <HugeiconsIcon
                              icon={ArrowDown01Icon}
                              size={14}
                              className={`shrink-0 text-muted-foreground transition-transform duration-150 group-hover:text-foreground ${isExpanded ? 'rotate-180' : ''}`}
                            />
                            <span className="truncate text-[13px] font-medium text-foreground" title={page.page || '/'}>
                              {page.page || '/'}
                            </span>
                            <span className="shrink-0 rounded-[5px] bg-muted px-1.5 py-px text-xs text-muted-foreground">
                              {sentenceLabel(page.page_group)}
                            </span>
                          </span>
                          <span className="w-[100px] shrink-0 text-right text-[13px] font-medium text-foreground tabular-nums">
                            {formatNumeric(page.total_submissions)}
                          </span>
                          <span className="hidden w-[200px] shrink-0 items-center gap-1.5 pl-6 text-xs text-muted-foreground sm:flex" title={rowForm?.target}>
                            {rowForm?.source && rowForm.source !== 'native' && (
                              <BrandIcon brand={rowForm.source as any} className="h-3 w-3 shrink-0 opacity-60" />
                            )}
                            <span className="truncate">{rowForm?.target || '-'}</span>
                          </span>
                          <span className="hidden w-[140px] shrink-0 text-right text-[13px] text-foreground tabular-nums md:block">
                            {formatNumeric(pickUniqueSubmissions(page))}
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="border-t border-border/60 bg-muted/40 px-[18px] py-4" onClick={(e) => e.stopPropagation()}>
                            {isLoadingDetails ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-4 gap-3">
                                  {[1, 2, 3, 4].map(i => (
                                    <Skeleton key={i} className="h-12 w-full" />
                                  ))}
                                </div>
                                <Skeleton className="h-32 w-full" />
                              </div>
                            ) : formDetails ? (
                              <div className="flex flex-col gap-4">
                                <div className="flex flex-wrap gap-x-6 gap-y-2">
                                  {[
                                    { label: 'Submissions', value: formatNumeric(formDetails.summary.total_submissions) },
                                    { label: 'Unique submissions', value: formatNumeric(pickUniqueSubmissions(formDetails.summary)) },
                                    { label: 'Total views', value: formatNumeric(formDetails.summary.page_views) },
                                    { label: <>Conversion {infoTip(FORM_CONV_RATE_TOOLTIP)}</>, value: `${(formDetails.summary.conversion_rate * 100).toFixed(1)}%` },
                                  ].map((m, i) => (
                                    <div key={i} className="flex flex-col gap-0.5">
                                      <span className="flex items-center gap-1 text-xs text-muted-foreground">{m.label}</span>
                                      <span className="text-sm font-semibold text-foreground tabular-nums">{m.value}</span>
                                    </div>
                                  ))}
                                </div>

                                <div className="flex flex-col gap-2 lg:flex-row">
                                  {formDetails.data.forms.length > 0 && (
                                    <div className="surface-raised flex min-w-0 flex-col lg:w-[320px] lg:shrink-0">
                                      <div className="flex items-center justify-between gap-2 px-[18px] py-3">
                                        <h4 className="text-[13px] font-semibold text-foreground">Forms on this page</h4>
                                        {formDetails.data.forms.length > 5 && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              setShowAllForms(!showAllForms)
                                            }}
                                            className="h-6 px-2 text-xs"
                                          >
                                            {showAllForms ? 'Show fewer' : `Show all ${formDetails.data.forms.length}`}
                                          </Button>
                                        )}
                                      </div>
                                      {(showAllForms ? formDetails.data.forms : formDetails.data.forms.slice(0, 5)).map((form, idx) => (
                                        <div key={idx} className="flex items-center gap-4 border-t border-border/60 px-[18px] py-2.5">
                                          <p className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">{form.target}</p>
                                          <div className="shrink-0 text-right">
                                            <p className="text-[13px] font-semibold text-foreground tabular-nums">{formatNumeric(form.submissions)}</p>
                                            <p className="text-xs text-muted-foreground tabular-nums">
                                              {formatNumeric(pickUniqueSubmissions(form))} unique submissions
                                            </p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Attribution / Traffic Sources */}
                                  {(() => {
                                    const labelMap = new Map<string, {
                                      primary: string
                                      secondary: string | null
                                      secondaryFull: string | null
                                      value: number
                                    }>()
                                    formDetails.data.forms.forEach(form => {
                                      form.utm_breakdown?.forEach(utm => {
                                        const { primary, secondary, secondaryFull, groupKey } = formatUtmLabel(utm)
                                        const existing = labelMap.get(groupKey)
                                        if (existing) {
                                          existing.value += utm.submissions
                                        } else {
                                          labelMap.set(groupKey, {
                                            primary,
                                            secondary,
                                            secondaryFull,
                                            value: utm.submissions,
                                          })
                                        }
                                      })
                                    })
                                    const detailTotal = Array.from(labelMap.values()).reduce((sum, u) => sum + u.value, 0)
                                    const topSources = Array.from(labelMap.values())
                                      .map(u => ({
                                        ...u,
                                        share: detailTotal > 0 ? u.value / detailTotal : 0,
                                      }))
                                      .sort((x, y) => y.value - x.value)
                                      .slice(0, 5)

                                    if (topSources.length === 0) return null
                                    return (
                                      <div className="surface-raised min-w-0 flex-1 p-[18px]">
                                        <TrafficSourcesList items={topSources} />
                                      </div>
                                    )
                                  })()}
                                </div>
                              </div>
                            ) : (
                              <p className="py-6 text-center text-[13px] text-muted-foreground">Failed to load form details</p>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {totalPages > 1 && (
                <Pagination
                  from={(currentPage - 1) * itemsPerPage + 1}
                  to={Math.min(currentPage * itemsPerPage, paginationTotal)}
                  total={paginationTotal}
                  extra={isFetchingMore ? <span className="ml-2">(loading…)</span> : null}
                  onPrev={() => handlePageChange(Math.max(1, currentPage - 1))}
                  onNext={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  prevDisabled={currentPage === 1}
                  nextDisabled={currentPage === totalPages || isFetchingMore}
                />
              )}
            </RaisedCard>
            )}
          </div>
    </TooltipProvider>
  )
}
